// src/features/landing/SectionEditorList.tsx
// Ordered list of a page's sections — add, remove, reorder, toggle hidden,
// edit content per type. Local state only; the parent persists via
// UpdateSections on an explicit Save.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Button } from '@react/components';
import { Select } from '@/components/Select';
import type { LandingSection, LandingSectionType } from '@core/types/landing';
import { SectionFieldsForm } from './SectionFieldsForm';
import { SECTION_TYPES, newSection, sectionLabel, type ScopeIds } from './helpers';

interface SectionEditorListProps {
  sections: LandingSection[];
  onChange: (next: LandingSection[]) => void;
  scope: ScopeIds;
}

export function SectionEditorList({ sections, onChange, scope }: SectionEditorListProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [addType, setAddType] = useState<LandingSectionType>('LANDING_SECTION_TYPE_TEXT');

  const reindex = (list: LandingSection[]) => list.map((s, i) => ({ ...s, order: i }));

  const addSection = () => {
    const next = reindex([...sections, newSection(addType, sections.length)]);
    onChange(next);
    setExpanded(next[next.length - 1].id);
  };

  const removeSection = (id: string) => onChange(reindex(sections.filter((s) => s.id !== id)));

  const move = (id: string, dir: -1 | 1) => {
    const idx = sections.findIndex((s) => s.id === id);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= sections.length) return;
    const next = [...sections];
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    onChange(reindex(next));
  };

  const updateSection = (next: LandingSection) => onChange(sections.map((s) => (s.id === next.id ? next : s)));

  if (sections.length === 0) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('landing.sections.empty')}</p>
        </div>
        <AddSectionBar addType={addType} setAddType={setAddType} onAdd={addSection} t={t} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sections.map((s, i) => (
        <div key={s.id} className="rounded-md border border-border">
          <div className="flex items-center gap-2 p-3">
            <div className="flex flex-col">
              <button type="button" disabled={i === 0} onClick={() => move(s.id, -1)} aria-label={t('landing.sections.moveUp')} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M8 3l5 6H3l5-6z" fill="currentColor" />
                </svg>
              </button>
              <button type="button" disabled={i === sections.length - 1} onClick={() => move(s.id, 1)} aria-label={t('landing.sections.moveDown')} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M8 13L3 7h10l-5 6z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <button type="button" onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="min-w-0 flex-1 text-left">
              <span className="text-xs font-medium uppercase text-muted-foreground">{sectionLabel(s.section_type)}</span>
              <p className="truncate text-sm text-foreground">{s.title || t('landing.sections.untitled')}</p>
            </button>
            <button
              type="button"
              onClick={() => updateSection({ ...s, hidden: !s.hidden })}
              aria-pressed={!s.hidden}
              aria-label={s.hidden ? t('landing.sections.showSection') : t('landing.sections.hideSection')}
              title={s.hidden ? t('landing.sections.showSection') : t('landing.sections.hideSection')}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {s.hidden ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M2 2l12 12M6.5 6.7A2 2 0 008 10a2 2 0 001.5-3.4M4 4.3C2.6 5.2 1.5 6.5 1 8c1.2 3 4 5 7 5 1.1 0 2.1-.3 3-.7M9.5 3.2A7.5 7.5 0 0115 8c-.3.8-.8 1.6-1.4 2.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M1 8c1.2-3 4-5 7-5s5.8 2 7 5c-1.2 3-4 5-7 5s-5.8-2-7-5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                  <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
                </svg>
              )}
            </button>
            <Button size="sm" variant="ghost" onClick={() => removeSection(s.id)}>
              {t('common.remove')}
            </Button>
          </div>

          {expanded === s.id && (
            <div className="space-y-3 border-t border-border p-3">
              <div className="grid grid-cols-2 gap-3">
                <Input label={t('landing.section.title')} value={s.title ?? ''} onChange={(e) => updateSection({ ...s, title: e.target.value })} />
                <Input label={t('landing.section.subtitle')} value={s.subtitle ?? ''} onChange={(e) => updateSection({ ...s, subtitle: e.target.value })} />
              </div>
              <SectionFieldsForm section={s} onChange={updateSection} scope={scope} />
            </div>
          )}
        </div>
      ))}

      <AddSectionBar addType={addType} setAddType={setAddType} onAdd={addSection} t={t} />
    </div>
  );
}

function AddSectionBar({
  addType,
  setAddType,
  onAdd,
  t,
}: {
  addType: LandingSectionType;
  setAddType: (t: LandingSectionType) => void;
  onAdd: () => void;
  t: (key: string) => string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Select aria-label={t('landing.sections.addSection')} value={addType} onChange={(e) => setAddType(e.target.value as LandingSectionType)} className="max-w-xs">
        {SECTION_TYPES.map((st) => (
          <option key={st} value={st}>
            {sectionLabel(st)}
          </option>
        ))}
      </Select>
      <Button size="sm" onClick={onAdd}>
        {t('landing.sections.addSection')}
      </Button>
    </div>
  );
}
